import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ensureOk, request, requestJson} from "../apiUtils";
import {
    type BillingConfig,
    type AuditRow,
    type BillingGranularity,
    type BillingInfo,
    type BillingInfoItem,
    type DailyUsageStat,
    type DailyUsageStats,
    type LegalEntity,
    type LegalEntityDraft,
    type LegalEntityLlmConfig,
    type LlmModel,
    type ManagedUser,
    type UserDraft,
} from "../../components/admin/AdminTypes";

type AdminUserDto = {
    id: number;
    name: string;
    role: string;
    preferredLocale: string;
    legalEntityId: number;
    legalEntityName: string;
};

type LegalEntityDto = {
    id: number;
    name: string;
    billingAddress: string;
    postalAddress: string | null;
    taxId: string;
};

type AuditSortDto = {
    field: string;
    direction: "asc" | "desc";
};

type CallRecordDto = {
    id: string;
    legalEntityId: number;
    userId: number;
    userName: string;
    inputTokens: number;
    outputTokens: number;
    model: string;
    response: string | null;
    latestMessage: string | null;
    language: string | null;
    systemPrompt: string | null;
    createdAt: string;
};

type CallRecordPageDto = {
    items: CallRecordDto[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    snapshotCreatedBefore: string;
};

type DailyUsageDto = {
    date: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    messagesSent: number;
};

type DailyUsageResponseDto = {
    cycleStart: string;
    cycleEnd: string;
    items: DailyUsageDto[];
};

type BillingInfoItemDto = {
    date: string;
    totalTokenAmount: number;
    price: number;
};

type BillingInfoResponseDto = {
    cycleStart: string;
    cycleEnd: string;
    items: BillingInfoItemDto[];
    granularity: BillingGranularity;
    pricePerToken?: number | null;
    customTokenPrice?: number | null;
};

type BillingConfigDto = {
    defaultPricePerToken: number;
};

type LlmModelDto = {
    id: number;
    name: string;
    isDefault: boolean;
};

type LegalEntityLlmConfigDto = {
    legalEntityId: number;
    legalEntityName: string;
    llmModelId: number;
    llmModelName: string;
};

type AuditSearchParams = {
    page: number;
    pageSize: number;
    legalEntityId?: string;
    userId?: string;
    createdAtFrom?: string;
    createdAtTo?: string;
    sortColumns: AuditSortDto[];
    snapshotCreatedBefore?: string;
};

type AuditResults = {
    items: AuditRow[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    snapshotCreatedBefore: string;
};

type BillingExportParams = {
    legalEntityId: string;
    startDate: string;
    endDate: string;
    timeGranularity: BillingGranularity;
};

export const adminUsersKey = ["admin", "users"] as const;
export const adminLegalEntitiesKey = ["admin", "legal-entities"] as const;
export const adminCallRecordsKey = ["admin", "call-records"] as const;
export const adminDailyUsageKey = ["admin", "daily-usage"] as const;
export const adminBillingInfoKey = ["admin", "billing-info"] as const;
export const adminBillingConfigKey = ["admin", "billing-config"] as const;
export const adminLlmModelsKey = ["admin", "llm-models"] as const;
export const adminLegalEntityLlmConfigsKey = ["admin", "legal-entity-llm-configs"] as const;

function mapLegalEntity(dto: LegalEntityDto): LegalEntity {
    return {
        id: String(dto.id),
        name: dto.name,
        billingAddress: dto.billingAddress,
        postalAddress: dto.postalAddress ?? "",
        taxId: dto.taxId,
    };
}

function mapBillingConfig(dto: BillingConfigDto): BillingConfig {
    return {
        defaultPricePerToken: dto.defaultPricePerToken,
    };
}

function mapLlmModel(dto: LlmModelDto): LlmModel {
    return {
        id: String(dto.id),
        name: dto.name,
        isDefault: dto.isDefault,
    };
}

function mapLegalEntityLlmConfig(dto: LegalEntityLlmConfigDto): LegalEntityLlmConfig {
    return {
        legalEntityId: String(dto.legalEntityId),
        legalEntityName: dto.legalEntityName,
        llmModelId: String(dto.llmModelId),
        llmModelName: dto.llmModelName,
    };
}

function mapManagedUser(dto: AdminUserDto): ManagedUser {
    return {
        id: String(dto.id),
        name: dto.name,
        role: dto.role,
        preferredLocale: dto.preferredLocale,
        legalEntityId: String(dto.legalEntityId),
    };
}

function mapAuditRow(dto: CallRecordDto): AuditRow {
    return {
        id: dto.id,
        legalEntityId: String(dto.legalEntityId),
        userId: String(dto.userId),
        userName: dto.userName,
        inputTokens: dto.inputTokens,
        outputTokens: dto.outputTokens,
        model: dto.model,
        response: dto.response ?? "",
        latestMessage: dto.latestMessage ?? "",
        language: dto.language ?? "",
        systemPrompt: dto.systemPrompt ?? "",
        createdAt: dto.createdAt,
    };
}

function mapDailyUsageStat(dto: DailyUsageDto): DailyUsageStat {
    return {
        date: dto.date,
        inputTokens: dto.inputTokens,
        outputTokens: dto.outputTokens,
        totalTokens: dto.totalTokens,
        messagesSent: dto.messagesSent,
    };
}

function mapBillingInfoItem(dto: BillingInfoItemDto): BillingInfoItem {
    return {
        date: dto.date,
        totalTokenAmount: dto.totalTokenAmount,
        price: dto.price,
    };
}

function createBillingInfoSearchParams(
    startDate: string,
    endDate: string,
    timeGranularity: BillingGranularity,
): URLSearchParams {
    return new URLSearchParams({
        startDate,
        endDate,
        timeGranularity,
    });
}

function getDownloadFilename(contentDisposition: string | null, fallbackFilename: string): string {
    if (!contentDisposition) {
        return fallbackFilename;
    }

    // Looks for: filename*=UTF-8''<value>
    // A prefix to decide, if the filename is RFC 5987 compliant
    const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

    if (utf8FilenameMatch?.[1]) {
        return decodeURIComponent(utf8FilenameMatch[1]);
    }

    // Looks for: filename=<value> or filename="<value>"
    // A Regex to prefevent accidental pulling of values outside the filename e.g. "filename=report.csv; size=1234"
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] ?? fallbackFilename;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
}

export function useAdminUsers(legalEntityId?: string) {
    return useQuery<AdminUserDto[], Error, ManagedUser[]>({
        queryKey: adminUsersKey,
        queryFn: () => requestJson<AdminUserDto[]>("/admin/users"),
        select: (data) =>
            data.map(mapManagedUser).filter((user) => !legalEntityId || user.legalEntityId === legalEntityId),
    });
}

export function useAdminLegalEntities() {
    return useQuery<LegalEntityDto[], Error, LegalEntity[]>({
        queryKey: adminLegalEntitiesKey,
        queryFn: () => requestJson<LegalEntityDto[]>("/admin/legal-entities"),
        select: (data) => data.map(mapLegalEntity),
    });
}

export function useAdminCallRecords(params?: AuditSearchParams) {
    return useQuery<CallRecordPageDto, Error, AuditResults>({
        queryKey: [...adminCallRecordsKey, params ?? null],
        enabled: Boolean(params?.legalEntityId),
        queryFn: async () => {
            const response = await request("/admin/call-records/search", {
                method: "POST",
                body: JSON.stringify({
                    page: params?.page ?? 0,
                    pageSize: params?.pageSize ?? 25,
                    legalEntityId: Number(params?.legalEntityId),
                    userId: params?.userId ? Number(params.userId) : undefined,
                    createdAtFrom: params?.createdAtFrom,
                    createdAtTo: params?.createdAtTo,
                    sortColumns: params?.sortColumns ?? [],
                    snapshotCreatedBefore: params?.snapshotCreatedBefore,
                }),
            });
            await ensureOk(response);
            return (await response.json()) as CallRecordPageDto;
        },
        select: (data) => ({
            items: data.items.map(mapAuditRow),
            page: data.page,
            pageSize: data.pageSize,
            totalElements: data.totalElements,
            totalPages: data.totalPages,
            snapshotCreatedBefore: data.snapshotCreatedBefore,
        }),
    });
}

export function useAdminDailyUsage(legalEntityId?: string, userId?: string, year?: string, month?: string) {
    return useQuery<DailyUsageResponseDto, Error, DailyUsageStats>({
        queryKey: [...adminDailyUsageKey, legalEntityId ?? null, userId ?? null, year ?? null, month ?? null],
        enabled: Boolean(legalEntityId),
        queryFn: () => {
            const searchParams = new URLSearchParams({legalEntityId: String(legalEntityId)});

            if (userId) {
                searchParams.set("userId", userId);
            }
            if (year) {
                searchParams.set("year", year);
            }
            if (month) {
                searchParams.set("month", month);
            }

            return requestJson<DailyUsageResponseDto>(`/admin/call-records/daily-usage?${searchParams.toString()}`);
        },
        select: (data) => ({
            cycleStart: data.cycleStart,
            cycleEnd: data.cycleEnd,
            items: data.items.map(mapDailyUsageStat),
        }),
    });
}

export function useAdminBillingInfo(
    legalEntityId?: string,
    startDate?: string,
    endDate?: string,
    timeGranularity?: BillingGranularity,
) {
    return useQuery<BillingInfoResponseDto, Error, BillingInfo>({
        queryKey: [
            ...adminBillingInfoKey,
            legalEntityId ?? null,
            startDate ?? null,
            endDate ?? null,
            timeGranularity ?? null,
        ],
        enabled: Boolean(legalEntityId && startDate && endDate && timeGranularity),
        queryFn: () => {
            const searchParams = createBillingInfoSearchParams(
                String(startDate),
                String(endDate),
                timeGranularity as BillingGranularity,
            );

            return requestJson<BillingInfoResponseDto>(
                `/admin/billing/info/${legalEntityId}?${searchParams.toString()}`,
            );
        },
        select: (data) => ({
            cycleStart: data.cycleStart,
            cycleEnd: data.cycleEnd,
            items: data.items.map(mapBillingInfoItem),
            granularity: data.granularity,
            pricePerToken: data.pricePerToken ?? data.customTokenPrice ?? null,
        }),
    });
}

export function useDownloadBillingCsv() {
    return useMutation({
        mutationFn: async ({legalEntityId, startDate, endDate, timeGranularity}: BillingExportParams) => {
            const searchParams = createBillingInfoSearchParams(startDate, endDate, timeGranularity);
            const response = await request(`/admin/billing/info/${legalEntityId}/csv?${searchParams.toString()}`, {
                headers: {
                    Accept: "text/csv",
                },
            });
            await ensureOk(response);

            const blob = await response.blob();
            const fallbackFilename = `billing_${legalEntityId}_${startDate}_${endDate}.csv`;
            const filename = getDownloadFilename(response.headers.get("content-disposition"), fallbackFilename);

            triggerBlobDownload(blob, filename);
        },
    });
}

export function useAdminBillingConfig() {
    return useQuery<BillingConfigDto, Error, BillingConfig>({
        queryKey: adminBillingConfigKey,
        queryFn: () => requestJson<BillingConfigDto>("/admin/billing/config"),
        select: mapBillingConfig,
    });
}

export function useAdminLlmModels() {
    return useQuery<LlmModelDto[], Error, LlmModel[]>({
        queryKey: adminLlmModelsKey,
        queryFn: () => requestJson<LlmModelDto[]>("/admin/llm-models"),
        select: (data) => data.map(mapLlmModel),
    });
}

export function useSyncAdminLlmModels() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async() => {
            const response = await request("/admin/llm-models/sync", {
                method: "POST",
            });
            await ensureOk(response);
        },
        onSuccess: async() => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: adminLlmModelsKey}),
                queryClient.invalidateQueries({queryKey: adminLegalEntityLlmConfigsKey}),
            ]);
        },
    });
}

export function useAdminLegalEntityLlmConfigs() {
    return useQuery<LegalEntityLlmConfigDto[], Error, LegalEntityLlmConfig[]>({
        queryKey: adminLegalEntityLlmConfigsKey,
        queryFn: () => requestJson<LegalEntityLlmConfigDto[]>("/admin/legal-entities/llm-configs"),
        select: (data) => data.map(mapLegalEntityLlmConfig),
    });
}

export function useUpdateDefaultLlmModel() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({llmModelId}: {llmModelId: string}) => {
            const response = await request("/admin/llm-models/default", {
                method: "PUT",
                body: JSON.stringify({
                    llmModelId: Number(llmModelId),
                }),
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminLlmModelsKey});
        },
    });
}

export function useUpsertLegalEntityLlmConfig() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({legalEntityId, llmModelId}: {legalEntityId: string; llmModelId: string}) => {
            const response = await request(`/admin/legal-entities/${legalEntityId}/llm-config`, {
                method: "PUT",
                body: JSON.stringify({
                    llmModelId: Number(llmModelId),
                }),
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminLegalEntityLlmConfigsKey});
        },
    });
}

export function useDeleteLegalEntityLlmConfig() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({legalEntityId}: {legalEntityId: string}) => {
            const response = await request(`/admin/legal-entities/${legalEntityId}/llm-config`, {
                method: "DELETE",
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminLegalEntityLlmConfigsKey});
        },
    });
}

export function useCreateLegalEntity() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (draft: LegalEntityDraft) => {
            const response = await request("/admin/legal-entities", {
                method: "POST",
                body: JSON.stringify(draft),
            });
            await ensureOk(response);
            const dto = (await response.json()) as LegalEntityDto;
            return mapLegalEntity(dto);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminLegalEntitiesKey});
        },
    });
}

export function useUpdateDefaultBillingPrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({pricePerToken}: {pricePerToken: string}) => {
            const response = await request("/admin/billing/config/default-price-per-token", {
                method: "PUT",
                body: JSON.stringify({
                    pricePerToken: pricePerToken.trim(),
                }),
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: adminBillingConfigKey}),
                queryClient.invalidateQueries({queryKey: adminLegalEntitiesKey}),
                queryClient.invalidateQueries({queryKey: adminBillingInfoKey}),
            ]);
        },
    });
}

export function useUpdateLegalEntityBillingPrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({legalEntityId, pricePerToken}: {legalEntityId: string; pricePerToken: string}) => {
            const response = await request(`/admin/billing/config/legal-entities/${legalEntityId}/price-per-token`, {
                method: "PUT",
                body: JSON.stringify({
                    pricePerToken: pricePerToken.trim(),
                }),
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: adminBillingConfigKey}),
                queryClient.invalidateQueries({queryKey: adminLegalEntitiesKey}),
                queryClient.invalidateQueries({queryKey: adminBillingInfoKey}),
            ]);
        },
    });
}

export function useDeleteLegalEntityBillingPrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({legalEntityId}: {legalEntityId: string}) => {
            const response = await request(`/admin/billing/config/legal-entities/${legalEntityId}/price-per-token`, {
                method: "DELETE",
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: adminBillingConfigKey}),
                queryClient.invalidateQueries({queryKey: adminLegalEntitiesKey}),
                queryClient.invalidateQueries({queryKey: adminBillingInfoKey}),
            ]);
        },
    });
}

export function useCreateUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (draft: UserDraft) => {
            const response = await request("/admin/users", {
                method: "POST",
                body: JSON.stringify({
                    name: draft.name,
                    password: draft.password,
                    preferredLocale: draft.preferredLocale,
                    legalEntityId: Number(draft.legalEntityId),
                }),
            });
            await ensureOk(response);
            const dto = (await response.json()) as AdminUserDto;
            return mapManagedUser(dto);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminUsersKey});
        },
    });
}

export function useSoftDeleteUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({userId}: {userId: string; legalEntityId: string}) => {
            const response = await request(`/admin/users/${userId}`, {
                method: "DELETE",
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: adminUsersKey});
        },
    });
}
